import React, { useEffect, useMemo, useRef, useState } from "react";
import { User } from "../../../../domain/User";
import { Attention, Badge, Button, Disclose, Table, Typography } from "@optimizely/axiom";
import { SidebarFooter } from "../../Sidebar/SidebarFooter";
import { useUserContext } from "../../../../providers/UserProvider";
import { AccessManagementUserFormInstanceTable } from "./AccessManagementUserFormInstanceTable";
import { useAccessList } from "../../../../hooks/useAccessList/useAccessList";
import { UserFormNameFields } from "./UserFormNameFields";
import { UserFormCreatedBy } from "./UserFormCreatedBy";
import { UserFormTabs } from "./UserFormTabs";
import { useAccessFlowUserFormContext } from "./AccessManagementUserFormProvider";
import {
    ATTRIBUTE_ROLES,
    ADMINCENTER_PRODUCT_ID,
    ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
    REMOVE_GROUP_ACCESS_SUCCESS_MESSAGE,
    ADD_GROUP_ACCESS_SUCCESS_MESSAGE,
    CUSTOM_GROUP_ACCESS_ACTION,
    PERMISSIONS_UPDATE_SUCCESS_MESSAGE,
    UNEXPECTED_ERROR,
    ALL_PAGES_SIZE,
    SWR_KEY_PREFIX
} from "../../../../constants/constants";
import { useUserGroups } from "../../../../hooks/useUserGroups/useUserGroups";
import { UserFormActivationStatus } from "./UserFormActivationStatus";
import { TableSkeleton } from "../../Skeleton/TableSkeleton";

import styles from "./AccessManagementUserForm.module.scss";
import { AccessManagementUserFormAccessHeader } from "./AccessManagementUserFormAccessHeader";
import { AddProductAccessPermission, AddProductAccessForm } from "../AddProductAccess/AddProductAccessForm";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../../feature-flags/flags";
import LimitByRole from "../../LimitByRole/LimitByRole";
import { AddGroupAccessForm } from "../AddGroupAccess/AddGroupAccessForm";
import { CustomGroupAccessConfirmationModal } from "../CustomGroupAccessConfirmation/CustomGroupAccessConfirmationModal";
import { datadogRum } from "@datadog/browser-rum";
import { Invitation } from "../../../../domain/Invitation";
import { useInvitations } from "../../../../hooks/useInvitations/useInvitations";
import { emitToast } from "../../../../lib/toaster-utils";
import { useAnalyticsTracking } from "../../../../hooks/useAnalyticsTracking/useAnalyticsTracking";
import { getGrantPermissionsPayload } from "../../../../lib/analytics-helpers";
import { useLocation } from "react-router-dom";
import { ANALYTICS_FLOWS } from "../../../../constants/analytics-constants";
import { useOrganization } from "../../../../hooks/useOrganization/useOrganization";
import { UserDetailsLocalLoginToggle } from "../../Toggles/UserDetailsLocalLoginToggle";
import { useLocalLoginSettings } from "../../../../hooks/useLocalLoginSettings/useLocalLoginSettings";
import { ProductPermission } from "../../../../domain/Permission.interface";
import { useProducts } from "../../../../hooks/useProducts/useProducts";
import { useUserAccessList } from "../../../../hooks/useUserAccessList/useUserAccessList";
import { ExternalUserIndicator } from "../../ExternalUserIndicator/ExternalUserIndicator";
import { AgentUserIndicator } from "../../AgentUserIndicator/AgentUserIndicator";
import { convertAccessToAccessDetailsByProductId } from "../../../../lib/access-helpers";
import { UserGroup } from "../../../../domain/UserGroup";
import { Collection } from "../../../../domain/Collection";
import useSWR from "swr";
import { useServicesContext } from "../../../../providers/ServicesProvider";
import { useUserTeams } from "../../../../hooks/useUserTeams/useUserTeams";

export const AccessManagementUserForm = ({
    onCancel,
    organizationId,
    showModal,
    user
}: {
    onCancel: () => void;
    organizationId?: string;
    showModal: React.Dispatch<React.SetStateAction<boolean>>;
    user: User;
}) => {
    const { products } = useProducts({ organizationId });
    const { email, homeOrganizationId: targetUserHomeOrgId, firstName, lastName } = user;
    const { enabled: enableSSOSupportedLocalLogin } = useFeatureFlag(Flags.SSO_SUPPORTED_LOCAL_LOGIN);
    const { enabled: showExternalStatus } = useFeatureFlag(Flags.ENABLE_EXTERNAL_USER_STATUS_DISPLAY);
    const { enabled: enableVirtualAgentUsers } = useFeatureFlag(Flags.ENABLE_VIRTUAL_AGENT_USERS);
    const { enabled: enableRebrand } = useFeatureFlag(Flags.ENABLE_REBRAND);
    const isVirtualUser = enableVirtualAgentUsers && user.isVirtual;
    const isExternalUser = targetUserHomeOrgId !== organizationId;
    const displayExternalStatusIcon = (showExternalStatus || enableVirtualAgentUsers) && isExternalUser;

    const { setUserGroupIds, setActiveField, updateCustomGroupAccess, setUpdateCustomGroupAccess } =
        useAccessFlowUserFormContext();
    const { sendTrackEvent } = useAnalyticsTracking();
    const { pathname } = useLocation();

    const [error, setError] = useState<string | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const [accessFormType, setAccessFormType] = useState<"Product" | "Custom Group" | undefined>(undefined);
    const { accessContext, canUserDoAction, instancePermissions, profile } = useUserContext();
    const { email: actingUserEmail } = profile || {};
    const userEmail = useRef<string | undefined>(user?.email);
    const [inProgress, setInProgress] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const isProductAccessFlow = pathname.includes("/access/");

    const { accessList: access, isLoading: accessListLoading } = useUserAccessList({
        organizationId,
        user,
        context: accessContext
    });

    const instanceIds = products?.flatMap((p) => p.instances.map((i) => i.id)) ?? [];

    const { accessList: instanceGroupAvailability, isLoading: availabilityLoading } = useAccessList({
        organizationId,
        instanceIds,
        context: accessContext
    });
    const existingAccessByProduct = convertAccessToAccessDetailsByProductId(access || []);

    const { addUserGroupUsers, deleteUserGroupUsers } = useUserGroups({ organizationId });
    const { createInvitation } = useInvitations({ organizationId: organizationId });
    const { organization } = useOrganization({ id: organizationId });
    const { localLoginSettings } = useLocalLoginSettings({ organizationId });

    // Feature flag for Opal auto-provisioning
    const { enabled: opalAutoProvisioningEnabled } = useFeatureFlag(Flags.ENABLE_OPAL_AUTO_PROVISIONING);
    const { enabled: teamGroupsEnabled } = useFeatureFlag(Flags.ENABLE_TEAM_GROUPS);

    const { teams: userTeams } = useUserTeams({
        organizationId,
        email,
        skip: !teamGroupsEnabled
    });

    const teamsByProduct = useMemo(() => {
        if (!userTeams.length || !products?.length) return [];

        const instanceMap = new Map<string, { productName: string; instanceName: string }>();
        for (const product of products) {
            for (const instance of product.instances) {
                instanceMap.set(instance.id, {
                    productName: product.name,
                    instanceName: instance.nickname || instance.name
                });
            }
        }

        const grouped = new Map<string, { id: string; name: string; instanceName: string }[]>();
        for (const team of userTeams) {
            let productName = "Other";
            let instanceName = "";
            for (const ip of team.instancePermissions) {
                const info = instanceMap.get(ip.instanceId);
                if (info) {
                    productName = info.productName;
                    instanceName = info.instanceName;
                    break;
                }
            }
            if (!grouped.has(productName)) {
                grouped.set(productName, []);
            }
            grouped.get(productName)!.push({ id: team.id, name: team.name, instanceName });
        }

        return Array.from(grouped.entries()).map(([productName, teams]) => ({
            productName,
            teams: teams.sort((a, b) => a.instanceName.localeCompare(b.instanceName))
        }));
    }, [userTeams, products]);

    // Fetch ALL user groups without context filtering to preserve existing access when adding new access
    const { userService } = useServicesContext();
    const allUserGroupsKey =
        organizationId && user?.email
            ? `${
                  SWR_KEY_PREFIX.GROUPS
              }/api/usergroups/?organizationId=${organizationId}&email=${user?.email}&${user?.userGroupIds.join(",")}`
            : null;

    const { data: allUserGroupsData, error: allUserGroupsError } = useSWR<Collection<UserGroup>, Error>(
        allUserGroupsKey,
        () => {
            return userService.getUserGroups({
                organizationId: organizationId!,
                emails: [user?.email!],
                size: ALL_PAGES_SIZE
            });
        }
    );

    const handleError = ({
        error,
        customMessage,
        extendedDataDogErrorInfo
    }: {
        error: Error | Error[];
        customMessage?: string;
        extendedDataDogErrorInfo?: { errorName?: string; [key: string]: any };
    }) => {
        const normalizedError = Array.isArray(error) ? error[0] : error;
        const normalizedErrorMessage = normalizedError?.message || UNEXPECTED_ERROR;
        const errorMessage = customMessage ? `${customMessage}: ${normalizedErrorMessage}` : normalizedErrorMessage;

        const dataDogErrorInfo = {
            error: normalizedError,
            email,
            organizationId,
            ...extendedDataDogErrorInfo
        };

        if (
            updateCustomGroupAccess?.action === CUSTOM_GROUP_ACCESS_ACTION.ADD ||
            updateCustomGroupAccess?.action === CUSTOM_GROUP_ACCESS_ACTION.REMOVE
        ) {
            setModalError(errorMessage);
        } else {
            setError(errorMessage);
        }

        datadogRum.addError(
            new Error(extendedDataDogErrorInfo?.errorName ?? "UNHANDLED ACCESS MANAGEMENT USER FORM ERROR"),
            dataDogErrorInfo
        );
    };

    const handleGroupsChanged = async (
        previousGroupId: string | undefined,
        updatedGroupId: string | undefined
    ): Promise<any | void> => {
        if (!updatedGroupId) {
            return deleteUserGroupUsers({ userGroupId: previousGroupId || "", userEmails: [email] });
        }

        return deleteUserGroupUsers({ userGroupId: previousGroupId || "", userEmails: [email] }).then(() =>
            addUserGroupUsers({ userGroupId: updatedGroupId, userEmails: [email] })
        );
    };

    const currentOrgIsTargetUsersHomeOrg = organizationId === targetUserHomeOrgId;

    const userPermissionsInTargetHomeOrg = instancePermissions?.find((instancePermission) => {
        return (
            instancePermission.organizationId === targetUserHomeOrgId &&
            instancePermission.productId === ADMINCENTER_PRODUCT_ID
        );
    });
    const canEditUserName = isVirtualUser
        ? false
        : currentOrgIsTargetUsersHomeOrg
        ? canUserDoAction({ action: ATTRIBUTE_ROLES.USERS.UPDATE })
        : !!userPermissionsInTargetHomeOrg &&
          canUserDoAction({ action: ATTRIBUTE_ROLES.USERS.UPDATE, roles: userPermissionsInTargetHomeOrg?.roles });

    const showEnableLocalLoginOption =
        enableSSOSupportedLocalLogin &&
        organization?.ssoEnabled &&
        localLoginSettings?.localLoginEnabled &&
        currentOrgIsTargetUsersHomeOrg;

    const closeAccessForm = () => {
        setAccessFormType(undefined);
    };

    const submitAddCustomGroupAccess = async () => {
        setInProgress(true);
        setModalError(null);

        await addUserGroupUsers({
            userGroupId: updateCustomGroupAccess?.id || "",
            userEmails: [email || ""]
        })
            .then(() => {
                const payload = getGrantPermissionsPayload({
                    userGroupIds: [updateCustomGroupAccess!.id!],
                    userGroupNames: [updateCustomGroupAccess!.name!],
                    email,
                    flow: isProductAccessFlow ? ANALYTICS_FLOWS.PRODUCT_ACCESS : ANALYTICS_FLOWS.USER_PAGE
                });
                sendTrackEvent(payload);
                setUpdateCustomGroupAccess(undefined);
                emitToast({ message: ADD_GROUP_ACCESS_SUCCESS_MESSAGE });
            })
            .catch((error) =>
                handleError({
                    error,
                    customMessage: "Failed to add new group access to user",
                    extendedDataDogErrorInfo: {
                        errorName: "FAILED TO ADD GROUP ACCESS",
                        component: "AccessManagementUserForm",
                        action: "handleConfirmationModalSubmit"
                    }
                })
            )
            .finally(() => {
                setInProgress(false);
                setAccessFormType(undefined);
            });
    };

    const submitRemoveCustomGroupAccess = async () => {
        setInProgress(true);
        setModalError(null);

        await handleGroupsChanged(updateCustomGroupAccess?.id, undefined)
            .then(() => {
                setUpdateCustomGroupAccess(undefined);
                emitToast({ message: REMOVE_GROUP_ACCESS_SUCCESS_MESSAGE });
            })
            .catch((error) =>
                handleError({
                    error,
                    customMessage: "Failed to remove group access from user",
                    extendedDataDogErrorInfo: {
                        errorName: "FAILED TO REMOVE GROUP ACCESS",
                        component: "AccessManagementUserForm",
                        action: "handleConfirmationModalSubmit"
                    }
                })
            )
            .finally(() => {
                setInProgress(false);
                setAccessFormType(undefined);
            });
    };

    const cancelUpdateCustomGroupAccess = () => {
        setUpdateCustomGroupAccess(undefined);
        setModalError(null);
    };

    const handleConfirmationModalSubmit = async () => {
        switch (updateCustomGroupAccess?.action) {
            case CUSTOM_GROUP_ACCESS_ACTION.ADD:
                return await submitAddCustomGroupAccess();
            case CUSTOM_GROUP_ACCESS_ACTION.REMOVE:
                return await submitRemoveCustomGroupAccess();
            default:
                return Promise.resolve().then(cancelUpdateCustomGroupAccess);
        }
    };

    useEffect(() => {
        setError(null);
        setModalError(null);
        setUserGroupIds(user.userGroupIds);

        if (user?.email !== userEmail.current) {
            userEmail.current = user?.email;
            setActiveField({ name: null });
        }
    }, [user, setUserGroupIds, setActiveField]);

    const onGroupAccessClose = () => {
        setAccessFormType(undefined);
        setModalError(null);
    };

    useEffect(() => {
        const handleEvent = (e: CustomEvent) => {
            const { action, id, name, userGroup } = e.detail;
            setUpdateCustomGroupAccess({ action, id, name, userGroup });
        };

        window.addEventListener(
            ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
            handleEvent as EventListenerOrEventListenerObject
        );

        return () => {
            window.removeEventListener(
                ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
                handleEvent as EventListenerOrEventListenerObject
            );
        };
    }, [setUpdateCustomGroupAccess]);

    const handleSave = async ({ permissions }: { permissions: AddProductAccessPermission[] }) => {
        setIsSaving(true);
        setError(null);

        if (!permissions || permissions.length === 0) {
            // TODO: Error?
            setIsSaving(false);
            return;
        }

        // Handle error state when fetching existing user groups
        if (allUserGroupsError) {
            setIsSaving(false);
            handleError({
                error: allUserGroupsError,
                customMessage: "Failed to load existing user groups"
            });
            return;
        }

        // Prevent submission while existing access is still loading
        if (access === undefined || allUserGroupsData === undefined) {
            const message = "Access information is still loading. Please wait and try again.";
            setIsSaving(false);
            setError(message);
            emitToast({ message });
            return;
        }

        const newUserGroups = permissions.map((p) => p.group).filter((g): g is UserGroup => g !== undefined);

        // Get ALL existing user groups (unfiltered by context) to preserve access across all scopes
        // This prevents accidentally revoking existing access when adding new access in a scoped view
        const existingUserGroups: UserGroup[] = allUserGroupsData?.items || [];

        // Merge existing and new groups, removing duplicates by group ID
        const allGroupsMap = new Map<string, UserGroup>();
        existingUserGroups.forEach((group) => allGroupsMap.set(group.id, group));
        newUserGroups.forEach((group) => allGroupsMap.set(group.id, group));
        const userGroups = Array.from(allGroupsMap.values());

        const invitation = new Invitation({
            firstName,
            lastName,
            email,
            requireAcceptance: false,
            organizationId: organizationId!,
            userGroups,
            createdBy: actingUserEmail!,
            created: new Date(),
            modified: new Date()
        });

        try {
            await createInvitation(invitation);
            setIsSaving(false);
            closeAccessForm();
            emitToast({ message: PERMISSIONS_UPDATE_SUCCESS_MESSAGE });
        } catch (error) {
            setIsSaving(false);
            handleError({
                error: error as Error,
                customMessage: "Failed to grant user access",
                extendedDataDogErrorInfo: {
                    errorName: "FAILED TO CREATE INVITATION",
                    component: "AccessManagementUserForm",
                    action: "handleSave"
                }
            });
        }

        // Check for Opal auto-provisioning if feature flag is enabled
        const opalPermission = permissions.find((p) => p.product?.id === process.env.REACT_APP_OPAL_PRODUCT_ID);
        const userGroupNames: string[] = [];
        const userGroupIds: string[] = [];
        userGroups.forEach((group) => {
            userGroupNames.push(group.name);
            userGroupIds.push(group.id);
        });
        let projectIds: string[] = [];
        let projectNames: string[] = [];
        permissions.forEach((p) => {
            if (p?.project) {
                if (p.project?.id && !projectIds.includes(p.project.id)) {
                    projectIds.push(p.project.id);
                }
                if (p.project?.name && !projectNames.includes(p.project.name)) {
                    projectNames.push(p.project.name);
                }
            }
        });
        setUserGroupIds([user.userGroupIds, userGroupIds].flat());

        const payload = getGrantPermissionsPayload({
            email,
            projectIds,
            projectNames,
            userGroupIds,
            userGroupNames,
            flow: isProductAccessFlow ? ANALYTICS_FLOWS.PRODUCT_ACCESS : ANALYTICS_FLOWS.USER_PAGE,
            opalAccessGranted: opalPermission?.role?.name || undefined,
            opalAccessGrantedViaProduct: opalAutoProvisioningEnabled && !!opalPermission && permissions.length > 1
        });

        sendTrackEvent(payload);
    };

    // For virtual teammates this becomes the "Details" tab; human users render it flat, so
    // their sidebar is unchanged.
    const detailsContent = (
        <>
            <UserFormNameFields user={user} disableEdit={!canEditUserName} hideLastName={isVirtualUser} />
            {enableVirtualAgentUsers && enableRebrand ? (
                <div className={styles["user-form-details-grid"]}>
                    <div>
                        <label className={`${styles["user-form-field-label__top-line"]} oui-label`}>Email</label>
                        <Typography type="body" tag="div">
                            {email}
                        </Typography>
                    </div>
                    <div data-testid="user-type-field">
                        <label className={`${styles["user-form-field-label__top-line"]} oui-label`}>User type</label>
                        <Typography type="body" tag="div">
                            {isVirtualUser ? (
                                <Badge color="purple" className="oui-badge--virtual-teammate" data-testid="vau-badge">
                                    Virtual Teammate
                                </Badge>
                            ) : displayExternalStatusIcon ? (
                                <Badge className={styles["badge-external"]} data-testid="user-type-badge">
                                    External
                                </Badge>
                            ) : (
                                <Badge className={styles["badge-internal"]} data-testid="user-type-badge">
                                    Internal
                                </Badge>
                            )}
                        </Typography>
                    </div>
                    <div />
                    <div data-testid="created-date-field">
                        <label className={`${styles["user-form-field-label__top-line"]} oui-label`}>Created date</label>
                        <Typography type="body" tag="div">
                            {user.created.toLocaleDateString("en-US", {
                                year: "numeric",
                                day: "numeric",
                                month: "short"
                            })}
                        </Typography>
                    </div>
                    {isVirtualUser && <UserFormCreatedBy email={email} />}
                </div>
            ) : (
                <>
                    <label className={`${styles["user-form-field-label"]} oui-label`}>Email</label>
                    <Typography type="body" tag="div">
                        {email}
                        {isVirtualUser ? (
                            <AgentUserIndicator className="push--left" fill="#080736" />
                        ) : (
                            displayExternalStatusIcon && <ExternalUserIndicator className="push--left" fill="#080736" />
                        )}
                    </Typography>
                </>
            )}
            {!isVirtualUser && currentOrgIsTargetUsersHomeOrg && (
                <UserFormActivationStatus user={{ email: user?.email }} setError={setError} showModal={showModal} />
            )}
            {!isVirtualUser && showEnableLocalLoginOption && <UserDetailsLocalLoginToggle handleError={handleError} />}
            <AccessManagementUserFormAccessHeader>
                <div>
                    <LimitByRole action={[ATTRIBUTE_ROLES.GROUPS.UPDATE, ATTRIBUTE_ROLES.GROUPS.MANAGE]} mode="hide">
                        <Button
                            className="push--right"
                            // eslint-disable-next-line react/style-prop-object
                            style="outline"
                            isDisabled={!!accessFormType}
                            whiteBackground
                            onClick={() => setAccessFormType("Product")}
                        >
                            Add Product Access
                        </Button>
                    </LimitByRole>
                    <LimitByRole action={[ATTRIBUTE_ROLES.GROUPS.UPDATE]} mode="hide">
                        <Button
                            // eslint-disable-next-line react/style-prop-object
                            style="outline"
                            isDisabled={!!accessFormType}
                            whiteBackground
                            onClick={() => setAccessFormType("Custom Group")}
                        >
                            Add Group Access
                        </Button>
                    </LimitByRole>
                </div>
            </AccessManagementUserFormAccessHeader>
            {accessFormType === "Product" && (
                <AddProductAccessForm
                    existingAccess={existingAccessByProduct}
                    className="push-double--top push-double--bottom"
                    onSubmit={handleSave}
                    onClose={closeAccessForm}
                    user={user}
                    isSaving={isSaving}
                />
            )}
            {accessFormType === "Custom Group" && (
                <AddGroupAccessForm
                    onClose={onGroupAccessClose}
                    organizationId={organizationId}
                    email={user.email}
                    customGroups
                />
            )}
            {!!updateCustomGroupAccess && (
                <CustomGroupAccessConfirmationModal
                    action={updateCustomGroupAccess?.action}
                    errorMessage={modalError}
                    isSaving={inProgress}
                    userGroup={updateCustomGroupAccess.userGroup!}
                    onClose={cancelUpdateCustomGroupAccess}
                    onSubmit={handleConfirmationModalSubmit}
                />
            )}
            {accessListLoading || availabilityLoading ? (
                <TableSkeleton />
            ) : (
                access?.map((item, itemIndex) => {
                    return (
                        <Disclose title={item.productName as string} childrenStyle="push--bottom" key={itemIndex}>
                            <AccessManagementUserFormInstanceTable
                                className="push--top push--bottom push--left"
                                productAccess={item}
                                onGroupsChanged={handleGroupsChanged}
                                productAvailability={instanceGroupAvailability?.find(
                                    (pp: ProductPermission) => pp.productId === item.productId
                                )}
                            />
                        </Disclose>
                    );
                })
            )}
            {teamGroupsEnabled && teamsByProduct.length > 0 && (
                <div data-testid="user-teams-section" data-cy="user-teams-section">
                    <div className="flex push-double--top push--bottom">
                        <Typography type="header3" tag="div">
                            Teams
                        </Typography>
                    </div>
                    {teamsByProduct.map(({ productName, teams }) => (
                        <Disclose title={productName} key={productName}>
                            <Table
                                density="loose"
                                // eslint-disable-next-line react/style-prop-object
                                style="rule-no-bottom-border"
                            >
                                <Table.THead>
                                    <Table.TR>
                                        <Table.TH sorting={{}}>Instance</Table.TH>
                                        <Table.TH sorting={{}}>Team</Table.TH>
                                    </Table.TR>
                                </Table.THead>
                                <Table.TBody>
                                    {teams.map((team, index) => {
                                        const isFirstOfInstance =
                                            index === 0 || teams[index - 1].instanceName !== team.instanceName;
                                        return (
                                            <Table.TR key={team.id}>
                                                <Table.TD>{isFirstOfInstance ? team.instanceName : ""}</Table.TD>
                                                <Table.TD>{team.name}</Table.TD>
                                            </Table.TR>
                                        );
                                    })}
                                </Table.TBody>
                            </Table>
                        </Disclose>
                    ))}
                </div>
            )}
        </>
    );

    return (
        <form data-cy="user-form">
            <div className="push-quad--sides">
                {error && (
                    <Attention className="push-double--bottom" alignment="left" type="bad-news">
                        {error}
                    </Attention>
                )}
                {/* Keyed on email so selecting a different VAU without closing the sidebar resets
                    the tab back to Details, the same way the form resets activeField above. */}
                {isVirtualUser ? (
                    <UserFormTabs key={email} email={email}>
                        {detailsContent}
                    </UserFormTabs>
                ) : (
                    detailsContent
                )}
            </div>
            <SidebarFooter onCancel={onCancel} cancelStyle="plain"></SidebarFooter>
        </form>
    );
};
